import * as Sentry from '@sentry/react';

import {
  ADDABLE_APP_LIMIT_COUNT,
  ADDABLE_APP_LIMIT_COUNT_TENANT,
  ADDABLE_COMMAND_LIMIT_COUNT,
  AXIOS_ERROR,
  AXIOS_ERROR_MESSAGE,
  HTTP_CLIENT_HEADER,
  HTTP_STATUS,
  INVITE_CAPACITY_LIMIT,
  INVITE_CAPACITY_LIMIT_LITE,
  MAX_TENANT_FILE_UPLOAD_SIZE_LITE,
  MODAL_NAMES,
  SERVER_ERROR,
} from '~/constants';
import NetworkError from '~/errors/NetworkError';
import i18n from '~/i18n';
import GA from '~/services/GA/GA';
import globalEventService, { EVENT_NAME } from '~/services/globalEventService';
import { channelsStore } from '~/stores/ChannelsStore';
import performanceData from '~/stores/data/performanceData';
import { mainDetailUiState } from '~/stores/DetailUiState';
import { globalSettingStore } from '~/stores/GlobalSettingStore';
import { api } from '~/stores/providers/Api2';
import { interTenantApi } from '~/stores/providers/InterTenantApi';
import logging from '~/stores/providers/logging';
import { uiState } from '~/stores/UiState';
import { getXHRMessage } from '~/utils/errorUtil';
import fileSizeFilter from '~/utils/fileSizeFilter';
import axios, { AxiosError } from 'axios';
import { get } from 'lodash';
import { always, cond, curry, equals, identity, T } from 'ramda';
import pathEq from 'ramda/es/pathEq';

import Actions from '../Actions';

class ErrorActions {
  tokens = [];

  constructor(private actions: Actions) {
    api.setErrorHandler(this.handleXHRError);
    interTenantApi.setErrorHandler(this.handleXHRError);

    globalEventService.addEventListener(EVENT_NAME.ERROR, (event: ErrorEvent) => {
      if (!globalSettingStore.isReal) {
        throw event.error ?? event;
      } else {
        logging.error('Script Error', event.error ?? event);
        GA.writeError(event.error ?? event);
      }
    });

    globalEventService.addEventListener(EVENT_NAME.UNHANDLED_REJECTION, (event: ErrorEvent | PromiseRejectionEvent) => {
      if ('reason' in event && axios.isCancel(event?.reason)) {
        return;
      }
      // @ts-ignore
      logging.error('Unhandled Rejection Error', event.error ?? event);
      // @ts-ignore
      GA.writeRejectionError(event.error ?? event);
      if (!globalSettingStore.isReal) {
        // @ts-ignore
        throw event.error ?? event;
      }
    });
  }

  public handleSendTokens = (tokens: string[]) => {
    if (this.tokens[0] === tokens[0]) {
      this.tokens = this.tokens.slice(tokens.length);
    }
  };

  handleXHRError = (error: AxiosError & { request: XMLHttpRequest }) => {
    if (axios.isCancel(error)) {
      GA.writeXhrError('취소', error.message);
    } else if (error.isAxiosError) {
      this.handleAxiosError(error);
    } else if (!error.response) {
      // with a status code that range of 2xx
      this.handleServerCustomError(error);
    } else {
      // with a status code that falls out of the range of 2xx
      this.handleHttpError(error);
    }
    const token = get(error, `config.headers.${HTTP_CLIENT_HEADER}`, '');
    delete performanceData[token];

    Sentry.captureException(new NetworkError());
  };

  private redirectTo = (path) => {
    window.location.href = path;
  };

  private redirectToHome = () => {
    this.redirectTo('/messenger');
  };

  private openSessionAlert = () => {
    uiState.openModal({ name: MODAL_NAMES.SESSION_OUT_MODAL });
  };

  private handleMovedPermanently = () => {
    this.redirectToHome();
  };

  private handleMovedTemporarily = () => {
    this.redirectToHome();
  };

  private handleUnauthorized = () => {
    this.openSessionAlert();
    uiState.setIsSessionOut(true);
    uiState.closeModal(MODAL_NAMES.RECONNECT);
    GA.writeCloseWebsocket('UNAUTHORIZED');
    this.actions.proxyActions.closeWebsockets();
  };

  private toErrorJson = (error) => {
    try {
      return JSON.stringify(error.toJson());
    } catch (error) {
      return '';
    }
  };

  private getReasonCode = (state) => {
    switch (state) {
      case HTTP_STATUS.TIMEOUT:
        return 'sto';
      case AXIOS_ERROR_MESSAGE.REQUEST_ABORTED:
        return 'ab';
      case '타임아웃':
        return 'to';
      case 'NETWORK ERROR':
        return 'ne';
      default:
        return 'etc';
    }
  };

  private getToken = (error, state) => {
    const token = get(error, `config.headers.${HTTP_CLIENT_HEADER}`, '');
    try {
      const prev = performanceData[token];
      return `${token}-${performance.now() - prev}-${this.getReasonCode(state)}`;
    } catch (e) {
      return token;
    }
  };

  private collectAxiosError = (status, error) => {
    const token = this.getToken(error, status);
    if (uiState.isOnline) {
      this.tokens.push(token);
    }
    GA.writeAxiosError(status, this.toErrorJson(error), token, get(error, 'config.url'));
  };

  private handleTimeout = (error) => {
    if (error.status === HTTP_STATUS.TIMEOUT) {
      this.collectAxiosError(HTTP_STATUS.TIMEOUT, error);
    } else if (error.message === AXIOS_ERROR_MESSAGE.REQUEST_ABORTED) {
      this.collectAxiosError(error.message, error);
    } else {
      this.collectAxiosError('타임아웃', error);
    }
    this.showErrorAlert(
      i18n.t('ECONNABORTED', { defaultValue: '일시적인 오류입니다.' }),
      i18n.t('ECONNABORTED', { defaultValue: '일시적인 오류입니다.' }),
      error
    );
  };

  private handleTooManyRedirect = () => {
    this.actions.notificationActions.showError({
      text: i18n.t('TooManyRedirectError', {
        defaultValue: '서버에 요청이 너무 많습니다. 잠시 후에 다시 시도해주세요.',
      }),
    });
  };

  private handleForbiddenACL = () => {
    this.redirectTo('/messenger/unauthorized');
  };

  private handleUnauthorizedIP = () => {
    this.redirectTo('/messenger/unauthorized_ip');
  };

  private handleChannelAlreadyExists = (res) => {
    const { result } = res;
    channelsStore.addChannel(result?.content);
    this.actions.channelActions.selectChannel(result?.content?.channelId);
  };

  private handleOverLimitUsageError = () => {
    const contentsChannelId = mainDetailUiState.contentsChannelId;
    this.actions.modalActions.openOverUsageAlert({ channelId: contentsChannelId });
  };

  private handleFileAclError = (res) => {
    const isUpload = !!res.config.onUploadProgress;
    const feature = isUpload ? i18n.t('fileUpload', '첨부파일 업로드') : i18n.t('fileDownload', '첨부파일 다운로드');

    this.actions.notificationActions.showError({
      text: i18n.t('FILE_ACL_ERROR', {
        defaultValue:
          '관리자가 {{feature}} 기능을 제한하였습니다.<br />자세한 내용은 내부 Dooray! 관리자에게 문의해 주세요.',
        feature,
      }),
    });
  };

  private handleHttpError = (error: Error) => {
    const isStatusEq = curry((status, error) => {
      return error?.response?.status === status;
    });

    cond([
      [isStatusEq(HTTP_STATUS.MOVED_PERMANENTLY) as (status: HTTP_STATUS) => boolean, this.handleMovedPermanently],
      [isStatusEq(HTTP_STATUS.MOVED_TEMPORARILY) as (status: HTTP_STATUS) => boolean, this.handleMovedTemporarily],
      [isStatusEq(HTTP_STATUS.UNAUTHORIZED) as (status: HTTP_STATUS) => boolean, this.handleUnauthorized],
      [isStatusEq(HTTP_STATUS.TIMEOUT) as (status: HTTP_STATUS) => boolean, this.handleTimeout],
      [isStatusEq(HTTP_STATUS.TOO_MANY_REDIRECT) as (status: HTTP_STATUS) => boolean, this.handleTooManyRedirect],
      [
        T,
        (error: Error) => {
          this.showErrorAlert(error?.message ?? '', get(error, 'response.status'), error);
        },
      ],
    ])(error);
  };

  private handleAxiosError = (error: AxiosError) => {
    const isCodeEq = curry((code: string, error: AxiosError) => {
      return error?.code === code;
    });

    cond([
      [isCodeEq(AXIOS_ERROR.ECONNABORTED) as (status: AxiosError) => boolean, this.handleTimeout],
      [
        T,
        (error: AxiosError) => {
          this.collectAxiosError('NETWORK ERROR', error);
          if (error?.response?.status) {
            this.handleHttpError(error);
            return;
          }
          this.actions.notificationActions.showError({
            text: i18n.t('NETWORK_ERROR', {
              defaultValue:
                '네트워크나 내부 방화벽에 의해 오류가 발생했습니다.<br>네트워크 상태를 확인하시거나 내부 관리자에게 문의해 주세요.',
            }),
          });
        },
      ],
    ])(error);
  };

  private handleServerCustomError = (error) => {
    const isCustomStatusEq = curry((status, error) => {
      return pathEq(['data', 'header', 'resultCode'], status, error);
    });
    cond([
      [
        isCustomStatusEq(SERVER_ERROR.AUTH_FORBIDDEN_ACL_SERVICE_ERROR) as (status: SERVER_ERROR) => boolean,
        this.handleForbiddenACL,
      ],
      [
        isCustomStatusEq(SERVER_ERROR.AUTH_FORBIDDEN_ACL_IP_ERROR) as (status: SERVER_ERROR) => boolean,
        this.handleUnauthorizedIP,
      ],
      [
        isCustomStatusEq(SERVER_ERROR.AUTH_FORBIDDEN_FILE_ACL_SERVICE_ERROR) as (status: SERVER_ERROR) => boolean,
        this.handleFileAclError,
      ],
      [
        isCustomStatusEq(SERVER_ERROR.AUTH_FORBIDDEN_FILE_ACL_IP_ERROR) as (status: SERVER_ERROR) => boolean,
        this.handleFileAclError,
      ],
      [
        isCustomStatusEq(SERVER_ERROR.SERVER_TASK_TRACKER_UNAUTHORIZED_SESSION_ERROR) as (
          status: SERVER_ERROR
        ) => boolean,
        this.handleUnauthorized,
      ],
      [
        isCustomStatusEq(SERVER_ERROR.CHANNEL_ALREADY_EXISTS_ERROR) as (status: SERVER_ERROR) => boolean,
        this.handleChannelAlreadyExists,
      ],
      [
        isCustomStatusEq(SERVER_ERROR.FILE_TENANT_USAGE_LIMIT_OVER_ERROR) as (status: SERVER_ERROR) => boolean,
        this.handleOverLimitUsageError,
      ],
      [
        isCustomStatusEq(SERVER_ERROR.FILE_MEMBER_USAGE_LIMIT_OVER_ERROR) as (status: SERVER_ERROR) => boolean,
        this.handleOverLimitUsageError,
      ],
      [isCustomStatusEq(SERVER_ERROR.SERVER_SIDE_APP_UNAVAILABLE_ERROR) as (status: SERVER_ERROR) => boolean, identity],
      [
        T,
        (error) => {
          return this.showErrorAlert(
            get(error, 'data.header.resultMessage'),
            get(error, 'data.header.resultCode'),
            error
          );
        },
      ],
    ])(error);
  };

  private getMessageParameter = (status) => {
    const { isLiteVersion } = globalSettingStore;
    const { messageDeletablePeriodMinutes } = globalSettingStore.messengerGeneralSetting;
    const channelCapacity = isLiteVersion ? INVITE_CAPACITY_LIMIT_LITE : INVITE_CAPACITY_LIMIT;
    const maxTenantFileUpload = fileSizeFilter(MAX_TENANT_FILE_UPLOAD_SIZE_LITE, 'MB');

    const {
      INTEGRATION_APP_NUMBER_OVER_LIMIT_ERROR,
      INTEGRATION_COMMAND_NUMBER_OVER_LIMIT_ERROR,
      CHANNEL_LOG_EXPIRED_DELETE_TIME_ERROR,
      CHANNEL_LOG_EXPIRED_MODIFY_TIME_ERROR,
      CHANNEL_DESCRIPTION_LENGTH_TOO_LONG_ERROR,
      CHANNEL_TITLE_LENGTH_TOO_LONG_ERROR,
      CHANNEL_CREATE_INVALID_CAPACITY_ERROR,
      CHANNEL_CAPACITY_IS_FULL_ERROR,
      INTEGRATION_APP_NUMBER_OVER_TENANT_LIMIT_ERROR,
      SERVER_FILE_OVER_LIMIT_OF_TOTAL_SIZE_PER_TENANT_ERROR,
    } = SERVER_ERROR;

    return cond<any[], any>([
      [equals(INTEGRATION_APP_NUMBER_OVER_LIMIT_ERROR), always({ limitCount: ADDABLE_APP_LIMIT_COUNT })],
      [equals(INTEGRATION_COMMAND_NUMBER_OVER_LIMIT_ERROR), always({ limitCount: ADDABLE_COMMAND_LIMIT_COUNT })],
      [equals(CHANNEL_LOG_EXPIRED_DELETE_TIME_ERROR), always({ minutes: messageDeletablePeriodMinutes })],
      [equals(CHANNEL_LOG_EXPIRED_MODIFY_TIME_ERROR), always({ minutes: messageDeletablePeriodMinutes })],
      [equals(CHANNEL_DESCRIPTION_LENGTH_TOO_LONG_ERROR), always({ max: 200 })],
      [equals(CHANNEL_TITLE_LENGTH_TOO_LONG_ERROR), always({ max: 30 })],
      [equals(CHANNEL_CREATE_INVALID_CAPACITY_ERROR), always({ max: channelCapacity })],
      [equals(CHANNEL_CAPACITY_IS_FULL_ERROR), always({ max: channelCapacity })],
      [
        equals(INTEGRATION_APP_NUMBER_OVER_TENANT_LIMIT_ERROR),
        always({
          max: globalSettingStore.isLiteVersion
            ? ADDABLE_APP_LIMIT_COUNT_TENANT.LITE
            : ADDABLE_APP_LIMIT_COUNT_TENANT.BASIC,
        }),
      ],
      [equals(SERVER_FILE_OVER_LIMIT_OF_TOTAL_SIZE_PER_TENANT_ERROR), always({ max: maxTenantFileUpload })],
      [T, always({ status })],
    ])(status);
  };

  private reportRealError = (message: string, url: string) => {
    logging.error('API 에러', message);
    GA.writeXhrError(message, url);
  };

  private showErrorAlert = curry((defaultMessage, status, error) => {
    const params = this.getMessageParameter(status);

    const xhrMessage = getXHRMessage(status, params);
    if (globalSettingStore.isReal) {
      this.reportRealError(xhrMessage || defaultMessage, error);
    }
    this.actions.notificationActions.showError({
      text: xhrMessage || `${i18n.t('TEMP_ERROR', '일시적인 오류입니다.')}(2)`,
    });
  });
}

export default ErrorActions;
